import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Check } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { SELECT_CLASS } from '../AudiencePicker';
import { apiFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import type { Campaign, TemplateListItem } from '../../types/api';
import {
  AI_LANGUAGE_LIST,
  type AiLanguage,
  type AiNewsletterReference,
  type AiNewsletterResult,
  type AiSegmentSuggestion,
  type AiSubjectSuggestion,
  type AiTranslateResult,
} from '../../types/ai';

type Skill = 'subject' | 'newsletter' | 'translate' | 'segment' | 'summary';

const SKILLS: readonly { id: Skill; label: string; help: string }[] = [
  { id: 'subject', label: 'Subject lines', help: 'Subject lines and preheaders per language.' },
  {
    id: 'newsletter',
    label: 'Newsletter draft',
    help: 'Writes your bullet points into the layout and tone of a mail you already sent, as a draft template. Nothing is sent.',
  },
  {
    id: 'translate',
    label: 'Translate a template',
    help: 'Makes a draft copy of a template per language (up to 3).',
  },
  {
    id: 'segment',
    label: 'Suggest a segment',
    help: 'Describes who you want; you open the result in the segment builder and save it there.',
  },
  {
    id: 'summary',
    label: 'Summarise a campaign',
    help: 'Plain-language summary of the totals of a sent campaign.',
  },
];

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'The assistant could not answer. Try again.';
}

function LanguageChecks({
  legend,
  value,
  onChange,
  max,
}: {
  legend: string;
  value: AiLanguage[];
  onChange: (next: AiLanguage[]) => void;
  max?: number;
}) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0">
      <legend className="mb-1.5 text-sm font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-1.5">
        {AI_LANGUAGE_LIST.map((l) => {
          const checked = value.includes(l.value);
          return (
            <label
              key={l.value}
              className={cn(
                'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
                checked
                  ? 'border-accent bg-accent-soft font-medium text-link'
                  : 'border-border text-muted hover:border-zinc-300 hover:text-text',
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                disabled={!checked && max !== undefined && value.length >= max}
                onChange={() =>
                  onChange(checked ? value.filter((v) => v !== l.value) : [...value, l.value])
                }
              />
              {checked ? <Check aria-hidden="true" className="size-3.5" /> : null}
              {l.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function TextArea({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <textarea
        id={id}
        className="min-h-[72px] w-full rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
        value={value}
        maxLength={2000}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function RunButton({
  pending,
  disabled,
  children,
  onClick,
}: {
  pending: boolean;
  disabled: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <Button size="sm" className="self-end" disabled={disabled || pending} onClick={onClick}>
      {pending ? 'Working…' : children}
    </Button>
  );
}

/** Every skill call counts against today's limit, so refresh the counter after it. */
function useAiMutation<TIn, TOut>(path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TIn) => apiFetch(path, { method: 'POST', body }) as Promise<TOut>,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['ai-settings'] }),
  });
}

function SubjectSkill() {
  const [topic, setTopic] = useState('');
  const [languages, setLanguages] = useState<AiLanguage[]>(['en']);
  const run = useAiMutation<
    { topic: string; languages: AiLanguage[]; count: number },
    { suggestions: AiSubjectSuggestion[] }
  >('/ai/skills/subject-lines');
  return (
    <div className="flex flex-col gap-3">
      <TextArea
        id="ai-subject-topic"
        label="What is the mail about?"
        value={topic}
        onChange={setTopic}
      />
      <LanguageChecks legend="Languages" value={languages} onChange={setLanguages} />
      <RunButton
        pending={run.isPending}
        disabled={topic.trim() === '' || languages.length === 0}
        onClick={() => run.mutate({ topic, languages, count: 3 })}
      >
        Suggest subject lines
      </RunButton>
      {run.isError ? <Alert>{errorText(run.error)}</Alert> : null}
      {run.data ? (
        <ul aria-label="Subject line suggestions" className="m-0 flex list-none flex-col gap-2 p-0">
          {run.data.suggestions.map((s, i) => (
            <li key={i} className="rounded-[10px] border border-border p-3 text-sm">
              <span className="text-xs uppercase text-muted">{s.language}</span>
              <div className="font-medium">{s.subject}</div>
              <div className="text-muted">{s.preheader}</div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface NewsletterRun {
  created: (AiNewsletterResult & { language: AiLanguage })[];
  failed: { language: AiLanguage; message: string }[];
}

function NewsletterSkill() {
  const [bullets, setBullets] = useState('');
  const [languages, setLanguages] = useState<AiLanguage[]>(['en']);
  const [name, setName] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const queryClient = useQueryClient();
  const references = useQuery({
    queryKey: ['ai-newsletter-references'],
    queryFn: () =>
      apiFetch<{ references: AiNewsletterReference[] }>('/ai/skills/newsletter/references'),
  });
  // One draft per language, one after the other (each is one request against today's limit).
  const run = useMutation({
    mutationFn: async (): Promise<NewsletterRun> => {
      const out: NewsletterRun = { created: [], failed: [] };
      const multi = languages.length > 1;
      for (const language of languages) {
        const label = AI_LANGUAGE_LIST.find((l) => l.value === language)?.label ?? language;
        try {
          const r = await apiFetch<AiNewsletterResult>('/ai/skills/newsletter', {
            method: 'POST',
            body: {
              bullets,
              language,
              ...(name.trim() ? { name: multi ? `${name.trim()} (${label})` : name.trim() } : {}),
              ...(referenceId ? { referenceCampaignId: referenceId } : {}),
            },
          });
          out.created.push({ ...r, language });
        } catch (err) {
          out.failed.push({ language, message: errorText(err) });
        }
      }
      return out;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['ai-settings'] }),
  });
  const sent = references.data?.references ?? [];
  const languageLabel = (l: AiLanguage) => AI_LANGUAGE_LIST.find((x) => x.value === l)?.label ?? l;
  return (
    <div className="flex flex-col gap-3">
      <TextArea id="ai-news-bullets" label="Bullet points" value={bullets} onChange={setBullets} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ai-news-reference">Layout and tone from</Label>
        <select
          id="ai-news-reference"
          className={SELECT_CLASS}
          value={referenceId}
          onChange={(e) => setReferenceId(e.target.value)}
        >
          <option value="">
            {references.isSuccess && sent.length === 0
              ? 'No sent mail yet: plain layout'
              : 'Newest sent mail in each language'}
          </option>
          {sent.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {r.sentAt ? ` (${new Date(r.sentAt).toLocaleDateString('en-GB')})` : ''}
            </option>
          ))}
        </select>
      </div>
      <LanguageChecks legend="Languages" value={languages} onChange={setLanguages} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ai-news-name">Template name (optional)</Label>
        <Input
          id="ai-news-name"
          value={name}
          maxLength={100}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {languages.length > 1 ? (
        <p className="m-0 text-xs text-muted">
          Makes one draft per language: {languages.length} requests.
        </p>
      ) : null}
      <RunButton
        pending={run.isPending}
        disabled={bullets.trim() === '' || languages.length === 0}
        onClick={() => run.mutate()}
      >
        {languages.length > 1
          ? `Create ${languages.length} draft templates`
          : 'Create draft template'}
      </RunButton>
      {run.data ? (
        <div role="status" className="flex flex-col gap-2 text-sm">
          {run.data.created.map((d) => (
            <div key={d.template.id} className="rounded-[10px] border border-border p-3">
              <span className="text-xs uppercase text-muted">{languageLabel(d.language)}</span>
              <p className="m-0">
                Draft template created:{' '}
                <Link to={`/skabeloner/${d.template.id}`} className="text-link">
                  {d.template.name}
                </Link>
              </p>
              <p className="m-0 mt-1">Suggested subject: {d.subject}</p>
              <p className="m-0 text-muted">Suggested preheader: {d.preheader}</p>
              {d.reference ? (
                <p className="m-0 mt-2 text-muted">
                  Layout and tone from: {d.reference.name}. Images, links and prices are unchanged
                  from that mail, so check them before you use the draft.
                  {d.keptTexts > 0
                    ? ` ${d.keptTexts} text${d.keptTexts === 1 ? ' was' : 's were'} left as in that mail.`
                    : ''}
                </p>
              ) : (
                <p className="m-0 mt-2 text-muted">
                  No sent mail to follow yet, so the draft uses a plain layout.
                </p>
              )}
            </div>
          ))}
          {run.data.failed.map((f) => (
            <Alert key={f.language}>
              {languageLabel(f.language)}: {f.message}
            </Alert>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TranslateSkill() {
  const [templateId, setTemplateId] = useState('');
  const [languages, setLanguages] = useState<AiLanguage[]>([]);
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => apiFetch<{ templates: TemplateListItem[] }>('/templates'),
  });
  const run = useAiMutation<{ templateId: string; languages: AiLanguage[] }, AiTranslateResult>(
    '/ai/skills/translate',
  );
  // Only mails written as HTML can be translated; block templates are edited per language.
  const options = (templates.data?.templates ?? []).filter((t) => t.kind === 'html');
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ai-translate-template">Template</Label>
        <select
          id="ai-translate-template"
          className={SELECT_CLASS}
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          <option value="">Choose a template</option>
          {options.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <LanguageChecks
        legend="Translate into (up to 3)"
        value={languages}
        onChange={setLanguages}
        max={3}
      />
      <RunButton
        pending={run.isPending}
        disabled={templateId === '' || languages.length === 0}
        onClick={() => run.mutate({ templateId, languages })}
      >
        Create draft translations
      </RunButton>
      {run.isError ? <Alert>{errorText(run.error)}</Alert> : null}
      {run.data ? (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-[10px] border border-border p-3 text-sm"
        >
          {run.data.created.map((c) => (
            <p key={c.template.id} className="m-0">
              Draft created:{' '}
              <Link to={`/skabeloner/${c.template.id}`} className="text-link">
                {c.template.name}
              </Link>
            </p>
          ))}
          {run.data.failed.map((f) => (
            <p key={f.language} className="m-0 text-danger-text">
              {f.language}: {f.message}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SegmentSkill() {
  const [description, setDescription] = useState('');
  const run = useAiMutation<{ description: string }, AiSegmentSuggestion>('/ai/skills/segment');
  return (
    <div className="flex flex-col gap-3">
      <TextArea
        id="ai-segment-description"
        label="Who do you want to reach?"
        value={description}
        onChange={setDescription}
      />
      <RunButton
        pending={run.isPending}
        disabled={description.trim() === ''}
        onClick={() => run.mutate({ description })}
      >
        Suggest a segment
      </RunButton>
      {run.isError ? <Alert>{errorText(run.error)}</Alert> : null}
      {run.data ? (
        <div role="status" className="rounded-[10px] border border-border p-3 text-sm">
          <p className="m-0">
            Suggested segment: <strong>{run.data.name}</strong>
          </p>
          <p className="m-0 mt-1 text-muted">
            Nothing is saved yet. Check the conditions in the segment builder and save it there.
          </p>
          <Link
            className="mt-2 inline-block text-link"
            to={`/indstillinger/segmenter?prefill=${encodeURIComponent(
              JSON.stringify({ name: run.data.name, filter: run.data.filter }),
            )}`}
          >
            Open in segment builder
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function SummarySkill() {
  const [campaignId, setCampaignId] = useState('');
  const campaigns = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiFetch<{ campaigns: Campaign[] }>('/campaigns'),
  });
  const run = useAiMutation<{ campaignId: string }, { summary: string }>(
    '/ai/skills/campaign-summary',
  );
  const sent = (campaigns.data?.campaigns ?? []).filter(
    (c) => c.status === 'sent' || c.status === 'sending',
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ai-summary-campaign">Campaign</Label>
        <select
          id="ai-summary-campaign"
          className={SELECT_CLASS}
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
        >
          <option value="">Choose a sent campaign</option>
          {sent.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <RunButton
        pending={run.isPending}
        disabled={campaignId === ''}
        onClick={() => run.mutate({ campaignId })}
      >
        Summarise
      </RunButton>
      {run.isError ? <Alert>{errorText(run.error)}</Alert> : null}
      {run.data ? (
        <p
          role="status"
          className="m-0 whitespace-pre-wrap rounded-[10px] border border-border p-3 text-sm"
        >
          {run.data.summary}
        </p>
      ) : null}
    </div>
  );
}

/** The skills with a fixed prompt and a fixed output shape (K7). Each one only returns text or creates a draft. */
export function AiSkills() {
  const [skill, setSkill] = useState<Skill>('subject');
  const current = SKILLS.find((s) => s.id === skill)!;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ai-skill">Skill</Label>
        <select
          id="ai-skill"
          className={SELECT_CLASS}
          value={skill}
          onChange={(e) => setSkill(e.target.value as Skill)}
        >
          {SKILLS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <p className="m-0 text-xs text-muted">{current.help}</p>
      </div>
      {skill === 'subject' ? <SubjectSkill /> : null}
      {skill === 'newsletter' ? <NewsletterSkill /> : null}
      {skill === 'translate' ? <TranslateSkill /> : null}
      {skill === 'segment' ? <SegmentSkill /> : null}
      {skill === 'summary' ? <SummarySkill /> : null}
    </div>
  );
}
